/* Verndale Custom Feature - Row Widget */
/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { getScrollbarWidth, mediaQueriesBP } from "@waytek/Utilities/variables";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    background = "background",
    useFullWidth = "useFullWidth",
    paddingMobile = "paddingMobile",
    paddingDesktop = "paddingDesktop",
    borderWidth = "borderWidth",
    borderStyle = "borderStyle",
    borderColor = "borderColor",
    useSameHeight = "useSameHeight",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.background]: string;
        [fields.useFullWidth]: boolean;
        [fields.borderWidth]: number;
        [fields.borderStyle]: string;
        [fields.borderColor]: string;
        [fields.paddingMobile]: string;
        [fields.paddingDesktop]: string;
        [fields.useSameHeight]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const horizontalPosition = ({ isFull }: any) => {
    if (isFull) {
        return "calc(((100vw - 100%) / 2) * -1)";
    }
    return "0";
};

const RowWrapper = styled.div<{ background: string; isFull: boolean; border?: string }>`
    position: relative;
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: ${({ isFull }) => (isFull ? "50%" : "0")};
        background: ${({ background }) => background};
        height: 100%;
        z-index: 0;
        border: ${({ border }) => border};
        width: ${({ isFull }) => (isFull ? `calc(100vw - ${getScrollbarWidth()})` : "100%")};
        margin-inline: ${({ isFull }) => (isFull ? "-50vw" : "0")};
    }
`;

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            background,
            useFullWidth,
            borderWidth,
            borderStyle,
            borderColor,
            paddingMobile,
            paddingDesktop,
            useSameHeight,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} fullHeight={useSameHeight} />
            </GridItem>,
        );
    }

    const container = <GridContainer gap={gap || 0}>{columnElements}</GridContainer>;

    if (background) {
        const border = `${borderWidth}px ${borderStyle} ${borderColor}`;
        return (
            <RowWrapper background={background} isFull={useFullWidth} border={border}>
                <StyledWrapper
                    css={css`
                        position: relative;
                        z-index: 9;
                        ${mediaQueriesBP.maxMD} {
                            padding: ${paddingMobile};
                        }
                        ${mediaQueriesBP.minMD} {
                            padding: ${paddingDesktop};
                        }
                    `}
                >
                    {container}
                </StyledWrapper>
            </RowWrapper>
        );
    }
    return (
        <StyledWrapper
            css={css`
                ${mediaQueriesBP.maxMD} {
                    padding: ${paddingMobile};
                }
                ${mediaQueriesBP.minMD} {
                    padding: ${paddingDesktop};
                }
            `}
        >
            {container}
        </StyledWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const customTab = {
    displayName: "Custom",
    sortOrder: 2,
};

const bgIsVisible = (item: HasFields) =>
    item.fields.background != null &&
    item.fields.background.length > 0 &&
    item.fields.background.toLowerCase() !== "#ffffff";

const isOneColumnLayour = (item: HasFields) =>
    item.fields.columns != null && (item.fields.columns === 12 || item.fields.columns[0] === 12);

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.background,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
                tab: customTab,
            },
            {
                name: fields.useFullWidth,
                displayName: "Use full width for background color",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
                isVisible: bgIsVisible,
                tab: customTab,
            },
            {
                name: fields.paddingDesktop,
                displayName: "Padding: Desktop",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "20px 20px",
                fieldType: "General",
                sortOrder: 3,
                isVisible: isOneColumnLayour,
                tab: customTab,
            },
            {
                name: fields.paddingMobile,
                displayName: "Padding: Mobile",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "20px 20px",
                fieldType: "General",
                sortOrder: 4,
                isVisible: isOneColumnLayour,
                tab: customTab,
            },
            {
                name: fields.borderWidth,
                displayName: "Border Width",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
                sortOrder: 5,
                validate: value => (/^\d$/.test(value) ? null : "Only numbers are available"),
                isVisible: bgIsVisible,
                tab: customTab,
            },
            {
                name: fields.borderStyle,
                displayName: "Border Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Solid", value: "solid" },
                    { displayName: "Dashed", value: "dashed" },
                    { displayName: "None", value: "none" },
                ],
                defaultValue: "solid",
                fieldType: "General",
                sortOrder: 6,
                isVisible: bgIsVisible,
                tab: customTab,
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                sortOrder: 7,
                isVisible: bgIsVisible,
                tab: customTab,
            },
            {
                name: fields.useSameHeight,
                displayName: "Use same height in the row",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
                tab: customTab,
            },
        ],
    },
};

export default widgetModule;
